import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import { Check, ShieldCheck, QrCode, Printer, RefreshCw, Globe, ChevronRight } from 'lucide-react';
import ThemePicker from '../components/ThemePicker';
import './Settings.css';

interface CategoryBudget {
    category: string;
    targetAmount: number;
}

const Settings: React.FC = () => {
    const { accessToken, logout, loading: authLoading } = useAuth();
    const history = useHistory();
    const [familySide, setFamilySide] = useState<'groom' | 'bride' | ''>('');
    const [totalBudget, setTotalBudget] = useState(0);
    const [categories, setCategories] = useState<CategoryBudget[]>([]);
    const [inviteToken, setInviteToken] = useState('');
    const [ceremonyReminderDays, setCeremonyReminderDays] = useState(3);
    const [taskReminderDays, setTaskReminderDays] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [publicAccess, setPublicAccess] = useState({
        gallery: false,
        schedule: false,
        vendors: false,
        guestWall: false,
        allowGuestUploads: false,
        disableScreenshots: false,
        lostFound: false,
        foodMenu: false
    });
    const [signedUrl, setSignedUrl] = useState('');
    const [accessKey, setAccessKey] = useState('');
    const [weddingName, setWeddingName] = useState('');
    const [loadingSignedUrl, setLoadingSignedUrl] = useState(false);

    // Password change state
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Profile for family side
                const profileRes = await fetch(`${API_BASE_URL}/api/admin/profile`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const profileData = await profileRes.json();
                if (profileRes.ok) {
                    setFamilySide(profileData.admin.familySide);
                }

                // Fetch Budget Settings
                const settingsRes = await fetch(`${API_BASE_URL}/api/settings/budget`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const settingsData = await settingsRes.json();
                if (settingsRes.ok) {
                    setTotalBudget(settingsData.settings.totalBudget);
                    setCategories(settingsData.settings.categoryBudgets);
                    setInviteToken(settingsData.settings.inviteToken || '');
                    setCeremonyReminderDays(settingsData.settings.notificationSettings?.ceremonyReminderDays ?? 3);
                    setTaskReminderDays(settingsData.settings.notificationSettings?.taskReminderDays ?? 1);
                } else if (settingsRes.status === 401) {
                    logout();
                    history.push('/login');
                }
                // Fetch Wedding for public access settings
                const weddingRes = await fetch(`${API_BASE_URL}/api/wedding/current`, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
                const weddingData = await weddingRes.json();
                if (weddingRes.ok && weddingData.wedding) {
                    setPublicAccess(weddingData.wedding.publicAccess || {
                        gallery: false,
                        schedule: false,
                        vendors: false,
                        guestWall: false,
                        allowGuestUploads: false,
                        disableScreenshots: false,
                        lostFound: false,
                        foodMenu: false
                    });
                    setWeddingName(weddingData.wedding.coupleName);
                }

                // Fetch Signed URL
                fetchSignedUrl();
            } catch (err) {
                console.error('Error fetching settings:', err);
            } finally {
                setLoading(false);
            }
        };

        if (!authLoading) {
            if (accessToken) {
                fetchData();
            } else {
                history.push('/login');
            }
        }
    }, [accessToken, authLoading, history, logout]);

    const handleAddCategory = () => {
        setCategories([...categories, { category: '', targetAmount: 0 }]);
    };

    const handleCategoryChange = (index: number, field: keyof CategoryBudget, value: string | number) => {
        const updated = [...categories];
        updated[index] = { ...updated[index], [field]: value };
        setCategories(updated);
    };

    const handleRemoveCategory = (index: number) => {
        setCategories(categories.filter((_, i) => i !== index));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/settings/budget`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    totalBudget,
                    categoryBudgets: categories.filter(c => c.category.trim() !== ''),
                    notificationSettings: {
                        ceremonyReminderDays,
                        taskReminderDays
                    }
                }),
            });

            if (response.ok) {
                alert('Settings saved successfully!');
            } else {
                alert('Failed to save settings');
            }
        } catch (err) {
            alert('Server error');
        } finally {
            setSaving(false);
        }
    };

    const generateInviteToken = async () => {
        setGenerating(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/settings/generate-invite`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await response.json();
            if (response.ok) {
                setInviteToken(data.token);
            } else {
                alert('Failed to generate token');
            }
        } catch (err) {
            alert('Error generating token');
        } finally {
            setGenerating(false);
        }
    };

    const fetchSignedUrl = async () => {
        setLoadingSignedUrl(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/wedding/signed-url`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const data = await res.json();
            if (res.ok) {
                setSignedUrl(data.signedUrl);
                setAccessKey(data.accessKey);
            }
        } catch (err) {
            console.error('Error fetching signed URL');
        } finally {
            setLoadingSignedUrl(false);
        }
    };

    const handlePublicAccessSave = async (updatedAccess: any) => {
        setSaving(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/wedding/public-access`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(updatedAccess),
            });
            if (res.ok) {
                setPublicAccess(updatedAccess);
                // No alert, just silent update or small toast
            }
        } catch (err) {
            console.error('Failed to update public access');
        } finally {
            setSaving(false);
        }
    };

    const handleRegenerateAccess = async () => {
        if (!window.confirm('A new QR code will be generated. All previously distributed QR codes will continue to work. Continue?')) return;

        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/wedding/regenerate-public-access`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (res.ok) {
                await fetchSignedUrl();
                alert('Access code regenerated successfully!');
            }
        } catch (err) {
            alert('Error regenerating code');
        } finally {
            setGenerating(false);
        }
    };

    const generatePDF = async () => {
        const doc = new jsPDF();

        // Helper to load image as base64
        const loadImage = (src: string): Promise<string> => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width;
                    canvas.height = img.height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0);
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = reject;
                img.src = src;
            });
        };

        // Add elegant border
        doc.setDrawColor(197, 160, 89); // Antique Gold
        doc.setLineWidth(1);
        doc.rect(10, 10, 190, 277);
        doc.setLineWidth(0.5);
        doc.rect(12, 12, 186, 273);

        try {
            // Add Logo
            const logoBase64 = await loadImage('/shadigram-logo.png');
            doc.addImage(logoBase64, 'PNG', 85, 20, 40, 40);
        } catch (err) {
            console.error('Failed to load logo for PDF', err);
            // Fallback to text if logo fails
            doc.setFont('times', 'bold');
            doc.setFontSize(28);
            doc.setTextColor(26, 32, 44);
            doc.text('Shadi Gram', 105, 40, { align: 'center' });
        }

        doc.setFont('times', 'italic');
        doc.setFontSize(14);
        doc.setTextColor(197, 160, 89);
        doc.text('Digital Heritage for Modern Weddings', 105, 65, { align: 'center' });

        // Add Message
        doc.setFont('times', 'normal');
        doc.setFontSize(18);
        doc.setTextColor(45, 55, 72);
        doc.text(`Scan to explore ${weddingName}'s Wedding`, 105, 95, { align: 'center' });
        doc.text('on Shadi Gram', 105, 103, { align: 'center' });

        // Add QR Code
        const canvas = document.querySelector('.qr-preview-box canvas') as HTMLCanvasElement;
        if (canvas) {
            const qrImage = canvas.toDataURL('image/png');
            doc.addImage(qrImage, 'PNG', 55, 120, 100, 100);
        } else {
            console.error('QR Canvas not found');
            alert('Failed to capture QR code. Please try again.');
            return;
        }

        // Add Footer Instructions
        doc.setFontSize(12);
        doc.setTextColor(113, 128, 150);
        doc.text('Instructions for Guests:', 105, 240, { align: 'center' });
        doc.setFontSize(10);
        doc.text('1. Open your phone camera or QR scanner', 105, 250, { align: 'center' });
        doc.text('2. Scan the code above to access the Gallery & Event details', 105, 255, { align: 'center' });
        doc.text('3. No download required - works in any mobile browser', 105, 260, { align: 'center' });

        doc.save(`${weddingName.replace(/\s+/g, '_')}_QR_Invitation.pdf`);
    };

    const handleChangePassword = async () => {
        setPasswordMessage({ type: '', text: '' });

        if (!currentPassword || !newPassword || !confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'All fields are required' });
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
            return;
        }

        if (newPassword.length < 6) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
            return;
        }

        setPasswordSaving(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/admin/change-password`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    currentPassword,
                    newPassword
                }),
            });

            const data = await response.json();
            if (response.ok) {
                setPasswordMessage({ type: 'success', text: 'Password successfully changed!' });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                setPasswordMessage({ type: 'error', text: data.message || 'Failed to change password' });
            }
        } catch (err) {
            setPasswordMessage({ type: 'error', text: 'Server error occurred' });
        } finally {
            setPasswordSaving(false);
        }
    };

    const inviteLink = `${window.location.origin}/join/${inviteToken}`;

    if (authLoading || loading) return <div className="settings-container">Loading...</div>;

    return (
        <div className={`admin-dashboard settings-container ${familySide ? `theme-${familySide}` : ''}`}>
            <div className="settings-header">
                <h1>Wedding Settings</h1>
                <p>Configure your budget, notifications, and guest invitations</p>
            </div>

            <div className="settings-grid">
                <div className="settings-section">
                    <ThemePicker />
                </div>

                <div className="settings-section">
                    <div className="section-title">
                        <span>✉️</span>
                        <h2>Guest Invitation Link</h2>
                    </div>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                        Generate a unique link for your side of the family. Guests will use this to request access.
                    </p>

                    {inviteToken ? (
                        <div className="invite-link-box" style={{ background: 'rgba(255,255,255,0.05)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--primary-accent)', marginBottom: '1.5rem' }}>
                            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '10px' }}>Your side's unique invite link:</div>
                            <div style={{ wordBreak: 'break-all', fontWeight: 'bold', color: 'var(--primary-accent)', marginBottom: '15px' }}>{inviteLink}</div>
                            <button className="add-expense-btn" onClick={() => {
                                navigator.clipboard.writeText(inviteLink);
                                alert('Link copied to clipboard!');
                            }}>Copy Link</button>
                        </div>
                    ) : (
                        <button className="save-settings-btn" onClick={generateInviteToken} disabled={generating}>
                            {generating ? 'Generating...' : 'Generate New Invite Link'}
                        </button>
                    )}
                    {inviteToken && (
                        <button style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', fontSize: '0.9rem', marginTop: '10px' }} onClick={generateInviteToken} disabled={generating}>
                            Refresh Token (Old link will stop working)
                        </button>
                    )}
                </div>

                <div className="settings-section">
                    <div className="section-title">
                        <span>💰</span>
                        <h2>Financial Planning</h2>
                    </div>

                    <div className="budget-input-group">
                        <label>Total Wedding Budget (Personal Side)</label>
                        <input
                            type="number"
                            className="large-input"
                            value={totalBudget}
                            onChange={(e) => setTotalBudget(Number(e.target.value))}
                            placeholder="e.g. 500000"
                        />
                    </div>

                    <div className="section-title" style={{ marginTop: '2rem' }}>
                        <span>📊</span>
                        <h2>Category Targets</h2>
                    </div>
                    <div className="categories-editor">
                        {categories.map((cat, index) => (
                            <div key={index} className="category-row">
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label>Category Name</label>
                                    <input
                                        type="text"
                                        value={cat.category}
                                        onChange={(e) => handleCategoryChange(index, 'category', e.target.value)}
                                        placeholder="e.g. Catering"
                                    />
                                </div>
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label>Target Amount</label>
                                    <input
                                        type="number"
                                        value={cat.targetAmount}
                                        onChange={(e) => handleCategoryChange(index, 'targetAmount', Number(e.target.value))}
                                        placeholder="0"
                                    />
                                </div>
                                <button className="delete-cat-btn" onClick={() => handleRemoveCategory(index)}>🗑️</button>
                            </div>
                        ))}

                        <button className="add-category-btn" onClick={handleAddCategory}>
                            + Add New Category
                        </button>
                    </div>

                    <button
                        className="save-settings-btn"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Financial Settings'}
                    </button>
                </div>

                <div className="settings-section public-access-card">
                    <div className="section-title">
                        <Globe className="section-icon" />
                        <h2>Public Access Manager</h2>
                        <span className="badge">New</span>
                    </div>

                    <p className="section-desc">
                        Decide which modules are visible to guests visiting via QR code or public link.
                        Public guests can view content but cannot post or edit.
                    </p>

                    <div className="permission-list">
                        {[
                            { id: 'gallery', label: 'Wedding Gallery', icon: '🖼️' },
                            { id: 'schedule', label: 'Event Schedule', icon: '📅' },
                            { id: 'vendors', label: 'Vendor List', icon: '🤝' },
                            { id: 'guestWall', label: 'Guest Wall (Wall of Love)', icon: '📝' },
                            { id: 'lostFound', label: 'Lost & Found Board', icon: '🔍' },
                            { id: 'foodMenu', label: 'Wedding Food Menu', icon: '🍽️' },
                            { id: 'allowGuestUploads', label: 'Allow Guests to Upload Photos', icon: '📸' },
                            { id: 'disableScreenshots', label: 'Privacy: Disable Direct Image Saving/Sharing', icon: '🛡️' }
                        ].map(item => (
                            <label key={item.id} className="permission-item">
                                <span className="item-info">
                                    <span className="item-icon">{item.icon}</span>
                                    <span className="item-label">{item.label}</span>
                                </span>
                                <div className="toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={(publicAccess as any)[item.id]}
                                        onChange={(e) => handlePublicAccessSave({ ...publicAccess, [item.id]: e.target.checked })}
                                    />
                                    <span className="slider"></span>
                                </div>
                            </label>
                        ))}
                    </div>

                    <div className="qr-management-area">
                        <div className="qr-preview-card">
                            <div className="modern-heritage-frame">
                                <div className="frame-corner top-left"></div>
                                <div className="frame-corner top-right"></div>
                                <div className="frame-corner bottom-left"></div>
                                <div className="frame-corner bottom-right"></div>
                                <div className="qr-preview-box">
                                    {signedUrl ? (
                                        <QRCodeCanvas
                                            value={`${window.location.origin}${signedUrl}`}
                                            size={180}
                                            level="M"
                                            includeMargin={false}
                                            imageSettings={{
                                                src: "/logo192.png",
                                                x: undefined,
                                                y: undefined,
                                                height: 40,
                                                width: 40,
                                                excavate: true,
                                            }}
                                        />
                                    ) : (
                                        <div className="qr-placeholder">
                                            {loadingSignedUrl ? <RefreshCw className="spin" /> : <QrCode size={48} />}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="qr-info">
                                <h3>Official QR Code</h3>
                                <p>Scan to explore digitally</p>
                                <div className="signed-status">
                                    <ShieldCheck size={14} />
                                    <span>HMAC Signed & Secure</span>
                                </div>
                            </div>
                        </div>

                        <div className="qr-actions">
                            <button className="btn-primary" onClick={generatePDF}>
                                <Printer size={18} />
                                Generate Printable PDF
                            </button>
                            <button className="btn-outline" onClick={handleRegenerateAccess} disabled={generating}>
                                <RefreshCw size={18} className={generating ? 'spin' : ''} />
                                Regenerate Secure Code
                            </button>
                        </div>
                    </div>
                </div>

                <div className="settings-section">
                    <div className="section-title">
                        <span>🔔</span>
                        <h2>Notification Preferences</h2>
                    </div>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                        Customize when you receive reminders for ceremonies and tasks.
                    </p>

                    <div className="budget-input-group">
                        <label>Ceremony Reminder (Days Before)</label>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                            Receive daily reminders starting this many days before each ceremony.
                        </p>
                        <input
                            type="number"
                            className="large-input"
                            value={ceremonyReminderDays}
                            onChange={(e) => setCeremonyReminderDays(Math.max(0, Math.min(7, Number(e.target.value))))}
                            placeholder="e.g. 3"
                            min="0"
                            max="7"
                        />
                        <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Range: 0-7 days</small>
                    </div>

                    <div className="budget-input-group" style={{ marginTop: '1.5rem' }}>
                        <label>Task Reminder (Days Before Due Date)</label>
                        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                            Get notified this many days before a task is due.
                        </p>
                        <input
                            type="number"
                            className="large-input"
                            value={taskReminderDays}
                            onChange={(e) => setTaskReminderDays(Math.max(0, Math.min(7, Number(e.target.value))))}
                            placeholder="e.g. 1"
                            min="0"
                            max="7"
                        />
                        <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Range: 0-7 days</small>
                    </div>

                    <button
                        className="save-settings-btn"
                        onClick={handleSave}
                        disabled={saving}
                        style={{ marginTop: '1.5rem' }}
                    >
                        {saving ? 'Saving...' : 'Save Notification Settings'}
                    </button>
                </div>

                <div className="settings-section">
                    <div className="section-title">
                        <span>🔒</span>
                        <h2>Security</h2>
                    </div>
                    <p style={{ color: 'var(--text-muted)', marginBottom: '1.5rem' }}>
                        Update your account password.
                    </p>

                    {passwordMessage.text && (
                        <div style={{
                            padding: '10px',
                            marginBottom: '1rem',
                            borderRadius: '8px',
                            background: passwordMessage.type === 'error' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(0, 255, 0, 0.1)',
                            color: passwordMessage.type === 'error' ? '#ff6b6b' : '#51cf66',
                            border: `1px solid ${passwordMessage.type === 'error' ? '#ff6b6b' : '#51cf66'}`
                        }}>
                            {passwordMessage.text}
                        </div>
                    )}

                    <div className="budget-input-group" style={{ marginBottom: '1rem' }}>
                        <label>Current Password</label>
                        <input
                            type="password"
                            className="large-input"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                        />
                    </div>

                    <div className="budget-input-group" style={{ marginBottom: '1rem' }}>
                        <label>New Password</label>
                        <input
                            type="password"
                            className="large-input"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                        />
                    </div>

                    <div className="budget-input-group">
                        <label>Confirm New Password</label>
                        <input
                            type="password"
                            className="large-input"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                        />
                    </div>

                    <button
                        className="save-settings-btn"
                        onClick={handleChangePassword}
                        disabled={passwordSaving}
                        style={{ marginTop: '1.5rem' }}
                    >
                        {passwordSaving ? 'Updating...' : 'Change Password'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Settings;

